import {
  LADDER_TRANSPORT_UNAVAILABLE,
  TIA_AGENT_ENVIRONMENT_CODES,
  TIA_DEFAULT_CPU_NOTE,
  TIA_READINESS_TEXT,
  TIA_VALIDATION_CODES,
  type PlcModelSource,
  type TiaGateCheck,
  type TiaProgramObRenameReason,
  type TiaReadiness,
  type TiaText,
  type TiaValidationMetadata,
  type TiaWorkMode,
} from '@realplc/types';

/**
 * What a TIA gate record means, in words.
 *
 * Moved out of `TiaValidationTrace.tsx` on 2026-09-23, when the execution
 * record's native compile row started reading the same record (TIA-UI-019):
 * two surfaces naming one blocker is exactly how they come to name it
 * differently. The runtime ships codes only; the wording lives here, once.
 */

export const REASON_LABELS: Record<string, [string, string]> = {
  not_siemens_target: ['Not a Siemens target', '非 Siemens 目标'],
  tia_sandbox_validate_forbidden: ['No sandbox-validate permission', '缺少沙箱验证权限'],
  manual_validation_required: ['Manual policy, trigger it yourself', '策略为手动触发'],
  validation_disabled: ['Validation disabled for this project', '该项目已关闭验证'],
  agent_unavailable_for_preferred_policy: ['No eligible RealPLC Agent', 'Agent 未授权给该项目'],
  agent_not_eligible_for_required_policy: ['Waiting for an eligible Agent', '等待 Agent 就绪'],
  no_applicable_policy: ['No applicable policy', '无适用策略'],
  // The headline already says the gate was not reached; this says why.
  static_validation_failed: ['Internal validation did not pass', '内部校验未通过'],
  // Reasons the explicitly requested (chat) validation can report.
  no_project: ['This chat is not bound to a project', '当前对话未绑定项目'],
  no_artifact: ['No program to validate yet', '当前没有可验证的程序'],
  agent_unavailable: ['No eligible RealPLC Agent', 'Agent 不可用'],
  // Ladder blocks reach TIA as SimaticML, which is not built yet.
  [LADDER_TRANSPORT_UNAVAILABLE]: ['Ladder blocks are not sent to TIA yet', '梯形图块暂不送 TIA 编译'],
  // The delivered program is itself checker-valid, but no gate ever ran for
  // it — not that internal validation failed (that is `static_validation_
  // failed`, kept below, and the two must never share a sentence: TA10f,
  // review 2026-09-29).
  gate_not_reached: ['The program passed the built-in checks, but this turn never sent it into TIA compilation', '程序已通过内置检查，但这一轮没能送进 TIA 编译'],
  // TIA's own gate-closing reasons (TA3, ai-runtime opennessValidateHandler.ts)
  // — one table for round-level and turn-level reading alike (review, I2,
  // 2026-09-29): `compileSkipReason.ts` falls back to this same table rather
  // than keeping its own copies of these strings.
  tia_run_closed: ['TIA has already passed a version this turn, so this turn sends it nothing more', 'TIA 已通过一版程序，本轮不再送编'],
  // Also the reason recorded for the *delivered* program when a more specific
  // earlier reason (a block, a timeout) is superseded by this one turn-wide
  // fact: TIA has nothing left to conclude this turn.
  tia_unavailable_this_turn: ['TIA has nothing left to conclude this turn, so this turn sends it nothing more', '本轮 TIA 已没有结论可给，不再送编'],
  validation_attempts_reserved: ['The last Run attempt was reserved for the final compile', '编译次数留给了最终编译'],
  // The final compile itself found the budget spent: nothing was held back for
  // it, so the sentence above would be false (review, 6, 2026-09-29).
  validation_attempts_exhausted: ['This turn\'s compile attempts are used up', '本轮编译次数已用完'],
  tia_run_blocked: ['The TIA run was blocked before it compiled this program', 'TIA 运行被阻止，没能编译这份程序'],
  tia_run_cancelled: ['The TIA run was cancelled before it compiled this program', 'TIA 运行被取消，没能编译这份程序'],
  tia_run_timed_out: ['The TIA validation timed out before it reached a verdict', 'TIA 验证超时，没有等到结论'],
  tia_run_refused: ['The TIA validation service refused this submission', 'TIA 验证服务拒绝了这次提交'],
  tia_gateway_unavailable: ['The TIA validation service was unavailable, so no verdict came back', 'TIA 验证服务不可用，没有等到结论'],
};

/**
 * Gate check labels live here, not in the runtime: a backend that ships prose
 * cannot be translated. The gate panel lists them, and a failed check whose
 * code has no words is named by them.
 */
export const CHECK_LABELS: Record<TiaGateCheck['id'], [string, string]> = {
  static_validation: ['Internal validation', '内部校验'],
  siemens_target: ['Siemens target', 'Siemens 目标'],
  sandbox_permission: ['Sandbox permission', '沙箱验证权限'],
  project_context: ['Project context', '项目上下文'],
  agent_eligibility: ['Agent eligibility', 'Agent 资格'],
  policy: ['Validation policy', '验证策略'],
};

/**
 * The way out through the TIA project picker above the input box (P2 T5):
 * choose a project there, or a new one.
 */
export type TiaPickerIntent = 'select' | 'new';

export interface AgentSituation {
  /** Collapsed-line phrase, English then Chinese. */
  label: [string, string];
  /** The one thing the reader can do about it. */
  action: [string, string];
  /** Choosing a project, or a new one, in the picker is that thing. */
  picker?: TiaPickerIntent;
}

/**
 * A situation in the words of a label and an explanation written elsewhere,
 * so that two surfaces say the same thing about the same state.
 */
function textSituation({ label, explanation }: { label: TiaText; explanation: TiaText }, picker?: TiaPickerIntent): AgentSituation {
  return {
    label: [label.en, label.zh],
    action: [explanation.en, explanation.zh],
    ...(picker ? { picker } : {}),
  };
}

/**
 * A situation in the words the readiness table gives its state
 * (`TIA_READINESS_TEXT`, written once in @realplc/types), so that the gate
 * under a turn and the TIA button say the same thing about the same state.
 */
function readinessSituation(state: TiaReadiness, picker?: TiaPickerIntent): AgentSituation {
  return textSituation(TIA_READINESS_TEXT[state], picker);
}

/**
 * Why the Agent's TIA connector declares no actions, when the Agent itself is
 * the reason: someone switched it off in the Agent, or the Agent quarantined
 * it. Readiness is capability_missing either way, and that state's words ask
 * for a newer Agent, which is wrong for both (T3 review). The TIA button says
 * these words (readTiaReadiness), and so does the gate, whose codes for them
 * are TIA_CONNECTOR_DISABLED and TIA_CONNECTOR_QUARANTINED (T8).
 */
export type TiaConnectorCause = 'disabled' | 'quarantined';

export const TIA_CONNECTOR_CAUSE_TEXT: Readonly<Record<TiaConnectorCause, { label: TiaText; explanation: TiaText }>> = {
  disabled: {
    label: { zh: '在线 · TIA 连接器已停用', en: 'Online · TIA connector disabled' },
    explanation: {
      zh: 'TIA 连接器在 Agent 的设置里被停用了，请在 Agent 里重新启用它。',
      en: 'The TIA connector is switched off in the Agent settings; switch it back on in the Agent.',
    },
  },
  quarantined: {
    label: { zh: '在线 · TIA 连接器已隔离', en: 'Online · TIA connector quarantined' },
    explanation: {
      zh: 'Agent 隔离了 TIA 连接器，请在 Agent 界面查看原因并恢复它。',
      en: 'The Agent has quarantined its TIA connector; the Agent window says why and how to restore it.',
    },
  },
};

/**
 * Agent eligibility codes, grouped by the situation the reader has to act on.
 *
 * Whether the Agent was *reachable* is the distinction that matters here. An
 * Agent the user can see connected in the toolbar, that still produced no
 * compile, reads as a broken product — and the one phrase the panel had for
 * this, "Agent 不可用", is false in exactly that case. Every entry that begins
 * with "online" is an Agent that answered and was still not usable.
 */
export const AGENT_SITUATIONS: Record<string, AgentSituation> = {
  offline: {
    label: ['Agent offline', 'Agent 离线'],
    action: [
      'Start the local RealPLC Agent and pair it to this project.',
      '启动本地 RealPLC Agent 并配对到该项目。',
    ],
  },
  // A readiness state since P2: the gate and the TIA button say it the same way.
  not_authorized: readinessSituation('not_authorized'),
  // An Agent still being probed: the runtime writes the readiness state into
  // the gate check's code (T9-2), where an old gateway wrote
  // PROJECT_NOT_OPEN, which keeps project_not_open's words.
  checking: readinessSituation('checking'),
  project_not_open: {
    label: ['Online, target project not open', 'Agent 在线，但目标项目未打开'],
    action: ['Open the target project in TIA Portal.', '在 TIA Portal 中打开目标项目。'],
  },
  project_mismatch: {
    label: ['Online, a different project is open', 'Agent 在线，但打开的是别的项目'],
    action: ['Switch TIA Portal to the target project.', '把 TIA Portal 切换到目标项目。'],
  },
  environment: {
    label: ['Online, local TIA environment not ready', 'Agent 在线，但本机 TIA 环境未就绪'],
    action: [
      'Check TIA Portal and Openness on the Agent machine.',
      '检查 Agent 所在机器的 TIA Portal 与 Openness 配置。',
    ],
  },
  capability: {
    label: ['Online, this Agent cannot validate SCL', 'Agent 在线，但不支持 SCL 验证'],
    action: ['Upgrade the local RealPLC Agent.', '升级本地 RealPLC Agent。'],
  },
  // The connector the Agent itself switched off or quarantined: no upgrade fixes it (T8).
  connector_disabled: textSituation(TIA_CONNECTOR_CAUSE_TEXT.disabled),
  connector_quarantined: textSituation(TIA_CONNECTOR_CAUSE_TEXT.quarantined),
  version: {
    label: ['Online, TIA version mismatch', 'Agent 在线，但 TIA 版本不匹配'],
    action: [
      'Match the Agent TIA version to the project.',
      '使 Agent 的 TIA 版本与项目一致。',
    ],
  },
  busy: {
    label: ['Online, busy with another job', 'Agent 在线，但正在执行其它任务'],
    action: ['Retry once the current job finishes.', '当前任务结束后重试。'],
  },
  auth: {
    label: ['Online, credentials expired', 'Agent 在线，但凭据已过期'],
    action: ['Re-pair the Agent.', '重新配对 Agent。'],
  },
  resolver_unavailable: {
    label: ['Agent status could not be resolved', '未能查询到 Agent 状态'],
    action: [
      'The eligibility check itself failed, so no Agent was ever selected. This is a backend fault, not an Agent fault.',
      '资格查询本身失败，因此根本没有选出 Agent。这是后端故障，不是 Agent 的问题。',
    ],
  },

  // Which project a validation targets (TIA 根治 P2): the readiness states the
  // target resolver's codes name, in the readiness table's words.
  tia_not_running: readinessSituation('tia_not_running'),
  no_project_selected: readinessSituation('no_project_selected', 'select'),
  project_not_found: readinessSituation('project_not_found', 'select'),
  upgrade_required: readinessSituation('upgrade_required'),

  // How a validation run ended without a compile (P2 E5 ①, E6 ⑧, E8, E11, E13).
  project_create_failed: {
    label: ['Could not create the TIA project', '新建 TIA 工程失败'],
    action: [
      'The Agent could not create a project for this validation. Choose an existing project, or validate again.',
      'Agent 没能为这次验证新建工程：请选一个已有工程，或重新验证。',
    ],
    picker: 'select',
  },
  project_version_unsupported: {
    label: ['Project saved by a newer TIA Portal', '工程由更新版本的 TIA 保存'],
    action: [
      "A newer TIA Portal than the Agent's saved this project, so the Agent cannot open it. Choose another project, or create a new one.",
      '保存这个工程的 TIA Portal 比 Agent 用的版本新，Agent 打不开它：请换一个工程，或新建工程。',
    ],
    picker: 'select',
  },
  // This and project_copy_failed get their buttons with T11 (see TiaValidationTrace).
  project_unsaved: {
    label: ['Project has unsaved changes', '工程有未保存的修改'],
    action: ['Save it in TIA Portal first, then validate.', '请先在 TIA 里保存，再验证。'],
  },
  project_changed_by_user: {
    label: ['Project changed in TIA', '工程已在 TIA 中修改'],
    action: [
      'Automatic repair stopped to preserve your edits. Review them in TIA; choose a new project if you want another automatic validation.',
      '为保留你在 TIA 中的修改，自动修复已停止。请在 TIA 中检查修改；如需再次自动验证，请选择新建工程。',
    ],
  },
  project_save_failed: {
    label: ['TIA project could not be saved', 'TIA 工程保存失败'],
    action: [
      'Check the available disk space and project permissions in TIA, then start a new validation.',
      '请检查磁盘空间及 TIA 工程权限，再发起新一轮验证。',
    ],
  },
  window_unavailable: {
    label: ['Visible TIA window unavailable', '无法使用可见的 TIA 窗口'],
    action: [
      'Run the Agent in the signed-in Windows desktop session and ensure the selected TIA Portal can open, then validate again.',
      '请在已登录的 Windows 桌面会话中运行 Agent，确认所选版本的 TIA Portal 可打开后重新验证。',
    ],
  },
  elevation_required: {
    label: ['Starting TIA requires administrator permission', '启动 TIA 需要管理员权限'],
    action: [
      'Windows refused to start TIA (Win32 740). Exit the Agent and its background host on the Agent PC, run the Agent as administrator, then validate again.',
      'Windows 拒绝启动 TIA（错误 740）。请在 Agent 所在电脑退出 Agent 及后台宿主，以管理员身份重新启动 Agent，再发起验证。',
    ],
  },
  uac_cancelled: {
    label: ['TIA startup authorization was cancelled', '已取消 TIA 启动授权'],
    action: [
      'Validate again and approve the Windows UAC prompt on the Agent PC. No compilation ran after authorization was cancelled.',
      '请重新发起验证，并在 Agent 所在电脑确认 Windows UAC 授权。取消授权后，本次没有执行编译。',
    ],
  },
  project_copy_failed: {
    label: ['Could not copy the project', '后台复制工程失败'],
    action: [
      'The Agent could not copy your open project in the background, so nothing was compiled and your project was not changed.',
      'Agent 没能在后台复制你打开的工程，这次没有编译，你的工程也没有被改动。',
    ],
  },
  // The Worker's own result, which the gateway renames and resends by itself:
  // on screen only when that did not happen.
  program_ob_conflict: {
    label: ['Program OB clash', '程序循环 OB 冲突'],
    action: [
      "The target project already holds a program-cycle OB that this program's would clash with. Choose another project, or create a new one.",
      '目标工程里已有会与本程序的程序循环 OB 冲突的块：请换一个工程，或新建工程。',
    ],
    picker: 'new',
  },
  program_ob_unresolved: {
    label: ['Program OB name clash', '程序循环 OB 重名'],
    action: [
      "The target project's OB1 holds a program, and this program's OB is also called Main. Choose another project, or create a new one.",
      '目标工程的 OB1 里有程序，本程序的 OB 也叫 Main：请换一个工程，或新建工程。',
    ],
    picker: 'new',
  },
  program_ob_ambiguous: {
    label: ['Two RealPLC program OBs in the project', '工程里有两个 RealPLC 程序循环 OB'],
    action: [
      "The target project holds two program-cycle OBs marked as RealPLC's, so which one this program replaces is unclear. Delete one in TIA Portal, or choose another project.",
      '目标工程里有两个带 RealPLC 标记的程序循环 OB，不知道该替换哪一个：请在 TIA 里删掉其中一个，或换一个工程。',
    ],
    picker: 'select',
  },
  safety_refused: {
    label: ['Refused by the safety rules', '超出安全底线，已拒绝'],
    action: [
      'RealPLC refused a step outside what a validation may change, so this validation did not run.',
      'RealPLC 拒绝了超出验证允许范围的操作，这次验证没有进行。',
    ],
  },
  // 「在 TIA 里打开」 on a run whose validation copy the Agent has cleaned up (T11).
  validation_copy_cleaned: {
    label: ['This validation copy has been cleaned up', '这份验证副本已清理'],
    action: [
      'The Agent cleaned up this run\'s validation copy, so there is nothing left to open. Validate again for a new one.',
      'Agent 已经清理了这次运行的验证副本，没有可打开的了；需要时重新验证，会生成新的副本。',
    ],
  },
  // 「在 TIA 里打开」 on a project the user's own TIA Portal has open: no fault,
  // it is open, so the page says it in the tone of a done action
  // (readTiaPickerOutcome).
  project_already_open: {
    label: ['Already open in TIA', '这个工程已经在 TIA 里开着了'],
    action: [
      'A TIA Portal window on the Agent machine already has it open, so the Agent opened no second one. Switch to that window.',
      'Agent 那台电脑上已经有 TIA 窗口开着它，Agent 没有再开第二个：切到那个窗口就行。',
    ],
  },
  // 「在 TIA 里打开」 and 「启动 TIA」 on a machine with no TIA Portal to start (T6/T7).
  portal_not_found: {
    label: ['TIA Portal not found', '没找到 TIA Portal'],
    action: [
      'The Agent found no TIA Portal on its machine to start, or could not start it. Make sure TIA Portal is installed there, then try again.',
      'Agent 在它那台电脑上没找到能启动的 TIA Portal，或没能启动它：请确认那台电脑装了 TIA Portal，再试一次。',
    ],
  },
  // Attaching to TIA (T7): TIA Portal was still asking whether to let the
  // Agent in when the Agent gave up. A blocker, never a compile failure.
  openness_access_timeout: {
    label: ['TIA is waiting for RealPLC\'s access to be allowed', 'TIA 在等待允许 RealPLC 访问'],
    action: [
      'TIA Portal asked whether to allow RealPLC\'s Openness access and nobody allowed it in time, so the Agent stopped. Allow the access in TIA Portal on the Agent machine, or reinstall the Agent (its installer adds the Worker to the Openness whitelist), then validate again.',
      'TIA Portal 弹出了是否允许 RealPLC 通过 Openness 访问的提示，一直没人允许，Agent 就放弃了：请在 Agent 那台电脑的 TIA 里点允许，或重新安装 Agent（安装程序会把 Worker 加进 Openness 白名单），然后重新验证。',
    ],
  },

  // The Agent's own environment on a validation job (TIA_AGENT_ENVIRONMENT_CODES),
  // which the gateway ends the run on as blocked, never as a compile failure.
  // Validating again is the way on where another try can go differently (the
  // gate panel's 「重新验证」); another project, where this one cannot be
  // compiled in.
  portal_start_failed: {
    label: ['The Agent could not start TIA Portal', 'Agent 没能启动 TIA Portal'],
    action: [
      'The Agent could not start the TIA Portal it validates in on its machine, so nothing was compiled. Check that TIA Portal starts there, then validate again.',
      'Agent 没能在它那台电脑上启动用来验证的 TIA Portal，这次没有编译：请确认 TIA Portal 在那台电脑上能正常启动，再重新验证。',
    ],
  },
  tia_attach_failed: {
    label: ['The Agent could not connect to TIA Portal', 'Agent 没能连上 TIA Portal'],
    action: [
      'The Agent started or found TIA Portal on its machine but could not connect to it, so nothing was compiled. Make sure TIA Portal there is not waiting on a dialog, then validate again.',
      'Agent 在它那台电脑上启动或找到了 TIA Portal，却没能连上它，这次没有编译：请确认那台电脑上的 TIA Portal 没有停在等待回答的对话框上，再重新验证。',
    ],
  },
  project_open_failed: {
    label: ['The Agent could not open the project', 'Agent 没能打开工程'],
    action: [
      'The Agent could not open the target project, or the copy it made of it, on its machine, so nothing was compiled. Choose another project, or create a new one.',
      'Agent 在它那台电脑上没能打开目标工程或它的副本，这次没有编译：请换一个工程，或新建工程。',
    ],
    picker: 'select',
  },
  plc_not_found: {
    label: ['No PLC in the project', '工程里没有 PLC'],
    action: [
      'The target project holds no PLC to compile the program in. Choose a project with a PLC, or create a new one.',
      '目标工程里没有可以编译程序的 PLC：请换一个有 PLC 的工程，或新建工程。',
    ],
    picker: 'select',
  },
  not_compilable: {
    label: ['The project\'s PLC cannot compile', '工程里的 PLC 不能编译'],
    action: [
      'TIA Portal offers no compile for the PLC software in the target project, so the program could not be checked there. Choose another project, or create a new one.',
      'TIA Portal 对目标工程里的 PLC 软件不提供编译，程序没法在那里检查：请换一个工程，或新建工程。',
    ],
    picker: 'select',
  },
  same_name_replace_failed: {
    label: ['Could not replace the blocks of the same name', '没能替换同名块'],
    action: [
      'The target project holds blocks with this program\'s names, and the Agent could not delete them to put the program in, so nothing was compiled. Validate again.',
      '目标工程里有与本程序同名的块，Agent 没能删掉它们换上本程序，这次没有编译：请重新验证。',
    ],
  },
  worker_exception: {
    label: ['The Agent\'s Worker stopped on an error', 'Agent 的 Worker 出错停止了'],
    action: [
      'The Worker that drives TIA Portal on the Agent machine stopped on an unexpected error, so there is no verdict on the program. Validate again; if it keeps happening, check the Agent window.',
      'Agent 那台电脑上驱动 TIA Portal 的 Worker 遇到意外错误停止了，这次没有得到结论：请重新验证；如果反复出现，请查看 Agent 界面。',
    ],
  },
  empty_worker_result: {
    label: ['The Worker returned no result', 'Worker 没有返回结果'],
    action: [
      'The Worker that drives TIA Portal on the Agent machine ended without a result, so whether the program compiles is still unknown. Validate again.',
      'Agent 那台电脑上驱动 TIA Portal 的 Worker 结束时没有返回结果，程序是否编译得过仍未知：请重新验证。',
    ],
  },
  // A source bundle the Agent rejects (a bundleHash mismatch, say) is built
  // wrong on RealPLC's side; the Agent never gets a program to compile, and
  // sending it the same bundle again cannot change that (TA10e, 2026-09-28).
  invalid_source_bundle: {
    label: ['The Agent rejected the package RealPLC sent', 'Agent 拒收了 RealPLC 发去的程序包'],
    action: [
      "The Agent rejected the package RealPLC sent, so nothing was compiled. This is a fault on RealPLC's side, not in your project or in TIA Portal; validating again will not change it.",
      'Agent 拒收了 RealPLC 发去的程序包，这次没有编译。这是 RealPLC 这边的问题，与你的工程和 TIA Portal 无关，重新验证也不会变。',
    ],
  },

  // How the gateway gives a run up before TIA says anything (opennessJobQueue
  // recordInfrastructureFailure), in the lower case it writes them.
  run_deadline_exceeded: {
    label: ['The validation ran out of time', '验证超时'],
    action: [
      'The run passed its time limit before TIA gave a verdict, so whether the program compiles is still unknown. Validate again.',
      '这次验证超过了时限，TIA 还没给出结论，程序是否编译得过仍未知：请重新验证。',
    ],
  },
  infrastructure_retry_exhausted: {
    label: ['The Agent kept failing to run the validation', 'Agent 多次未能完成验证'],
    action: [
      'The job failed on the Agent several times, for reasons outside the program, and retries stopped. Check the Agent window, then validate again.',
      '验证作业在 Agent 一侧多次失败，原因不在程序本身，已停止重试：请查看 Agent 界面，再重新验证。',
    ],
  },
  infrastructure_retry_blocked: {
    label: ['The validation could not be retried', '验证无法重试'],
    action: [
      'A job that failed on the Agent could not be sent again. Validate again.',
      '在 Agent 一侧失败的验证作业没能重新派发：请重新验证。',
    ],
  },
  validation_indeterminate: {
    label: ['No verdict: the compile timed out twice', '没有结论：编译两次超时'],
    action: [
      'The same program timed out twice in TIA, so automatic retries stopped for someone to look into it.',
      '同一份程序在 TIA 里两次超时，已停止自动重试，需要人工排查。',
    ],
  },
  // The reason opennessJobQueue.cancelRun records unless given another.
  user_cancelled: {
    label: ['The validation was cancelled', '验证已取消'],
    action: [
      'It was cancelled before TIA gave a verdict. Validate again when you need one.',
      '它在 TIA 给出结论之前被取消了，需要时请重新验证。',
    ],
  },

  // How the gateway refuses to make a run or an attempt (tiaValidationRunService):
  // the page's create-run route reads the program first (the three artifact
  // codes); adding an attempt to a run checks the run (the three run codes);
  // storing a project choice needs the conversation.
  run_terminal: {
    label: ['This validation has already ended', '这次验证已经结束'],
    action: [
      'It ended before this request, so it takes no further attempt. Validate again to start a new one.',
      '它在这次请求之前就结束了，不能再追加尝试：请重新验证，开始新的一次。',
    ],
  },
  attempt_in_progress: {
    label: ['A compile of this validation is still under way', '这次验证还有一次编译在进行'],
    action: [
      'The run already has an attempt being prepared, queued or compiled, so no second one was started. Wait for it to end.',
      '这次验证已有一次尝试正在准备、排队或编译，没有再开第二次：请等它结束。',
    ],
  },
  attempt_budget_exhausted: {
    label: ['This validation has used all its attempts', '这次验证的尝试次数已用完'],
    action: [
      'The run has compiled as many versions of the program as one run may, so it takes no more. Validate again to start a new one.',
      '这次验证已编译过一次验证所允许的最多版本数，不再接受新的版本：请重新验证，开始新的一次。',
    ],
  },
  artifact_not_found: {
    label: ['The program to validate was not found', '没找到要验证的程序'],
    action: [
      'This project has no stored copy of the program to validate, so nothing was sent to TIA. Generate the program again in this project, then validate.',
      '这个项目里没有这份程序的存档，没有发给 TIA：请在这个项目里重新生成程序，再验证。',
    ],
  },
  artifact_empty: {
    label: ['The program to validate is empty', '要验证的程序是空的'],
    action: [
      'The stored program holds no SCL code, so nothing was sent to TIA. Generate the program again, then validate.',
      '存档的程序里没有 SCL 代码，没有发给 TIA：请重新生成程序，再验证。',
    ],
  },
  unsupported_platform: {
    label: ['Not a Siemens SCL program', '不是 Siemens SCL 程序'],
    action: [
      'TIA Portal compiles Siemens SCL, and this program was generated for another platform, so nothing was sent to TIA. To validate it in TIA, set the target to Siemens and generate it again.',
      'TIA Portal 编译的是 Siemens SCL，这份程序是为别的平台生成的，没有发给 TIA：要在 TIA 里验证，请把目标设为 Siemens 后重新生成。',
    ],
  },
  session_not_found: {
    label: ['The conversation was not found', '没找到这个对话'],
    action: [
      'RealPLC could not find this conversation; it may have been deleted. Reload the page, then try again.',
      'RealPLC 找不到这个对话，它可能已被删除：请刷新页面后再试。',
    ],
  },
};

/**
 * The situation each code names. A gate check's `code` is a blocker code or a
 * bare eligibility state, and a blocked run ends on a code of the same
 * namespace (`TIA_VALIDATION_CODES`: a code means the same thing wherever it
 * lands) or on one of the Agent's own (`TIA_AGENT_ENVIRONMENT_CODES`), so one
 * table serves both, and the gateway's refusal of a request (tiaRefusalWords).
 */
export const AGENT_CODE_SITUATIONS: Record<string, string> = {
  AGENT_OFFLINE: 'offline',
  ALL_AGENTS_OFFLINE: 'offline',
  AGENT_NOT_ONLINE: 'offline',
  offline: 'offline',
  [TIA_VALIDATION_CODES.AGENT_TARGET_NOT_AUTHORIZED]: 'not_authorized',
  AGENT_ORG_MISMATCH: 'not_authorized',
  checking: 'checking',
  PROJECT_NOT_OPEN: 'project_not_open',
  project_not_open: 'project_not_open',
  PROJECT_FINGERPRINT_MISMATCH: 'project_mismatch',
  project_mismatch: 'project_mismatch',
  TIA_NOT_INSTALLED: 'environment',
  PUBLIC_API_NOT_FOUND: 'environment',
  OPENNESS_PERMISSION_MISSING: 'environment',
  WORKER_MISSING: 'environment',
  DIAGNOSTICS_FAILED: 'environment',
  AGENT_NOT_READY: 'environment',
  environment_not_ready: 'environment',
  CAPABILITY_MISSING: 'capability',
  VALIDATE_SANDBOX_CAPABILITY_MISSING: 'capability',
  capability_missing: 'capability',
  TIA_CONNECTOR_DISABLED: 'connector_disabled',
  TIA_CONNECTOR_QUARANTINED: 'connector_quarantined',
  TIA_VERSION_MISMATCH: 'version',
  version_incompatible: 'version',
  AGENT_BUSY: 'busy',
  busy: 'busy',
  CREDENTIAL_INVALID: 'auth',
  auth_required: 'auth',
  RESOLVER_UNAVAILABLE: 'resolver_unavailable',
  // An old Agent's word that TIA is not running (P2 E1 ③): not an environment
  // fault, and starting TIA, or upgrading the Agent, is the way out.
  [TIA_VALIDATION_CODES.TIA_NOT_RUNNING]: 'tia_not_running',
  [TIA_VALIDATION_CODES.TIA_PROJECT_NOT_SELECTED]: 'no_project_selected',
  [TIA_VALIDATION_CODES.TIA_PROJECT_REF_NOT_FOUND]: 'project_not_found',
  [TIA_VALIDATION_CODES.AGENT_UPGRADE_REQUIRED]: 'upgrade_required',
  [TIA_VALIDATION_CODES.TIA_PROJECT_CREATE_FAILED]: 'project_create_failed',
  [TIA_VALIDATION_CODES.TIA_PROJECT_VERSION_UNSUPPORTED]: 'project_version_unsupported',
  [TIA_VALIDATION_CODES.TIA_PROJECT_UNSAVED]: 'project_unsaved',
  [TIA_VALIDATION_CODES.TIA_PROJECT_CHANGED_BY_USER]: 'project_changed_by_user',
  [TIA_VALIDATION_CODES.TIA_PROJECT_SAVE_FAILED]: 'project_save_failed',
  [TIA_VALIDATION_CODES.TIA_WINDOW_UNAVAILABLE]: 'window_unavailable',
  [TIA_VALIDATION_CODES.TIA_ELEVATION_REQUIRED]: 'elevation_required',
  [TIA_VALIDATION_CODES.TIA_UAC_CANCELLED]: 'uac_cancelled',
  [TIA_VALIDATION_CODES.TIA_PROJECT_COPY_FAILED]: 'project_copy_failed',
  [TIA_VALIDATION_CODES.PROGRAM_OB_CONFLICT]: 'program_ob_conflict',
  [TIA_VALIDATION_CODES.PROGRAM_OB_CONFLICT_UNRESOLVED]: 'program_ob_unresolved',
  [TIA_VALIDATION_CODES.PROGRAM_OB_AMBIGUOUS]: 'program_ob_ambiguous',
  [TIA_VALIDATION_CODES.TIA_SAFETY_REFUSED]: 'safety_refused',
  [TIA_VALIDATION_CODES.TIA_VALIDATION_COPY_CLEANED]: 'validation_copy_cleaned',
  [TIA_VALIDATION_CODES.TIA_PROJECT_ALREADY_OPEN]: 'project_already_open',
  [TIA_VALIDATION_CODES.TIA_PORTAL_NOT_FOUND]: 'portal_not_found',
  [TIA_VALIDATION_CODES.TIA_OPENNESS_ACCESS_TIMEOUT]: 'openness_access_timeout',
  // Lower case, as the Worker and CloudBridge spell them and the run ends on them.
  [TIA_AGENT_ENVIRONMENT_CODES.portal_start_failed]: 'portal_start_failed',
  [TIA_AGENT_ENVIRONMENT_CODES.tia_attach_failed]: 'tia_attach_failed',
  [TIA_AGENT_ENVIRONMENT_CODES.project_open_failed]: 'project_open_failed',
  [TIA_AGENT_ENVIRONMENT_CODES.plc_not_found]: 'plc_not_found',
  [TIA_AGENT_ENVIRONMENT_CODES.not_compilable]: 'not_compilable',
  [TIA_AGENT_ENVIRONMENT_CODES.same_name_replace_failed]: 'same_name_replace_failed',
  [TIA_AGENT_ENVIRONMENT_CODES.worker_exception]: 'worker_exception',
  [TIA_AGENT_ENVIRONMENT_CODES.empty_worker_result]: 'empty_worker_result',
  [TIA_AGENT_ENVIRONMENT_CODES.invalid_source_bundle]: 'invalid_source_bundle',
  run_deadline_exceeded: 'run_deadline_exceeded',
  infrastructure_retry_exhausted: 'infrastructure_retry_exhausted',
  infrastructure_retry_blocked: 'infrastructure_retry_blocked',
  validation_indeterminate: 'validation_indeterminate',
  user_cancelled: 'user_cancelled',
  // The job queue refuses a job past its attempt's dispatch budget
  // (opennessJobQueue.create), the budget its own retries spend: the same end.
  ATTEMPT_DISPATCH_LIMIT: 'infrastructure_retry_exhausted',
  RUN_TERMINAL: 'run_terminal',
  ACTIVE_ATTEMPT_EXISTS: 'attempt_in_progress',
  ATTEMPT_BUDGET_EXHAUSTED: 'attempt_budget_exhausted',
  ARTIFACT_NOT_FOUND: 'artifact_not_found',
  ARTIFACT_EMPTY: 'artifact_empty',
  UNSUPPORTED_PLATFORM: 'unsupported_platform',
  SESSION_NOT_FOUND: 'session_not_found',
};

/**
 * The situation a code names. A run's failure reason can arrive lower-cased
 * (the gateway's inferBlockedReason lower-cases the codes it knows), so a
 * code that is not a key as written is tried in capitals.
 */
export function situationForCode(code: string | undefined): AgentSituation | undefined {
  if (!code) return undefined;
  const key = AGENT_CODE_SITUATIONS[code] ?? AGENT_CODE_SITUATIONS[code.toUpperCase()];
  return key ? AGENT_SITUATIONS[key] : undefined;
}

/**
 * What a TIA request the gateway or the Agent refused says, English then
 * Chinese: its code's situation, and what to do, when the code has words;
 * else `fallback`, with the code beside it, so a refusal nothing words yet is
 * never silent. For the buttons that act on a run or an Agent (T11).
 */
export function tiaRefusalWords(code: string | undefined, fallback: [string, string]): [string, string] {
  const situation = situationForCode(code);
  if (situation) return [`${situation.label[0]}: ${situation.action[0]}`, `${situation.label[1]}：${situation.action[1]}`];
  return code ? [`${fallback[0]} (${code})`, `${fallback[1]}（${code}）`] : fallback;
}

/**
 * The policy check's code, in words, English then Chinese.
 *
 * That code is the gate's own reason — the runtime's `enforceValidationPolicy`
 * (validation_disabled, manual_validation_required, no_applicable_policy) or,
 * for an explicitly requested validation, no_artifact / agent_unavailable — so
 * it is named by `REASON_LABELS`, as on the collapsed line. A code without
 * words stays raw. a799a8da 之后门禁列表里只剩这一行还露原始代码。
 */
export function readPolicyLabel(check: TiaGateCheck): [string, string] | undefined {
  return check.id === 'policy' && check.code ? REASON_LABELS[check.code] : undefined;
}

export type TiaOutcome = 'passed' | 'failed' | 'waiting' | 'unvalidated';

export function readOutcome(tv: TiaValidationMetadata): TiaOutcome {
  // `waiting` is set by the runtime only on the metadata it writes for the
  // waiting turn itself; the gate decision is what survives onto the persisted
  // result, so a reloaded wait has to be read from the trace or it reads as a
  // plain unvalidated delivery.
  if (tv.waiting || tv.gate?.decision === 'wait') return 'waiting';
  // A blocked or cancelled run never reached a compile: the Agent, the
  // project or a person stopped it before TIA said anything about the program
  // (an unsaved project, a copy that failed). Reading it as a failed compile
  // blamed the program.
  if (tv.skipped || tv.status === 'blocked' || tv.status === 'cancelled') return 'unvalidated';
  return tv.ok ? 'passed' : 'failed';
}

/** The first thing that kept TIA from compiling, as the reader is told it. */
export interface TiaBlock {
  /** The collapsed-line phrase, English then Chinese. */
  label: [string, string];
  /** The one thing the reader can do about it, when the words say one. */
  action?: [string, string];
  /** The code behind it, kept for whoever is debugging. */
  code?: string;
  /**
   * Whether words name the code itself. When none do, the code stays on
   * screen beside the check's name, so a new blocker is never silent.
   */
  worded: boolean;
  /** Choosing a project in the picker, or a new one, is the way out. */
  picker?: TiaPickerIntent;
}

function blockOf(situation: AgentSituation, code: string | undefined): TiaBlock {
  return {
    label: situation.label,
    action: situation.action,
    ...(code ? { code } : {}),
    worded: true,
    ...(situation.picker ? { picker: situation.picker } : {}),
  };
}

/** The gate's own reason in words, or the record's, when either has them. */
function reasonLabel(tv: TiaValidationMetadata): [string, string] | undefined {
  if (tv.gate?.reason && REASON_LABELS[tv.gate.reason]) return REASON_LABELS[tv.gate.reason];
  return tv.reason ? REASON_LABELS[tv.reason] : undefined;
}

/**
 * The code a run that ended without a compile ended on: the failure reason of
 * a blocked or cancelled run, or the error the runtime copied from it
 * (opennessValidateHandler).
 */
function readRunBlockCode(tv: TiaValidationMetadata): string | undefined {
  if (tv.status !== 'blocked' && tv.status !== 'cancelled') return undefined;
  const run = tv.detail?.run;
  const failureReason = run && typeof run === 'object' ? (run as { failureReason?: unknown }).failureReason : undefined;
  const code = typeof failureReason === 'string' ? failureReason : tv.error;
  return code?.trim() || undefined;
}

/** A run that ended on a code nothing here words, for a reason nothing words either: the reader learns only that TIA said nothing. */
const NO_VERDICT: [string, string] = ['TIA validation ended without a verdict', 'TIA 验证未得出结论'];

function checkBlock(check: TiaGateCheck, tv: TiaValidationMetadata): TiaBlock {
  const situation = check.id === 'agent_eligibility' ? situationForCode(check.code) : undefined;
  if (situation) return blockOf(situation, check.code);
  const policy = readPolicyLabel(check);
  if (policy) return { label: policy, code: check.code, worded: true };
  // Static validation, sandbox permission and project context fail without a
  // code: the gate's reason says why it stopped there.
  if (!check.code) return { label: reasonLabel(tv) ?? CHECK_LABELS[check.id], worded: true };
  return { label: CHECK_LABELS[check.id], code: check.code, worded: false };
}

/**
 * What kept TIA from compiling, and only the first thing (门禁只显示第一个阻塞
 * 原因): the gate panel under the turn says it, and the execution record's
 * native compile row names it through readBlockerLabel.
 *
 * 1. The gate's first failed check: an Agent check by its situation, the
 *    policy check by its reason (readPolicyLabel), a check without a code by
 *    the gate's reason. A code without words keeps the check's name.
 * 2. Else the code a blocked run ended on. A code without words is named by
 *    the record's reason, in the words the round row gives that reason too
 *    (`compileSkipText`): a runtime timeout, a refusal, an unreachable
 *    gateway. Else it is said to have ended without a verdict. Either way the
 *    code shows itself as a check's does: a timeout's message, a dispatch
 *    error the gateway recorded as it came.
 * 3. Else the gate's reason, or the record's own, when either has words.
 *
 * A situation outranks a reason because the reason can be plainly wrong:
 * `agent_unavailable_for_preferred_policy` reads "Agent 未授权给该项目" for an
 * Agent that was online and merely had the wrong project open.
 */
export function readFirstBlock(tv: TiaValidationMetadata): TiaBlock | undefined {
  const failed = tv.gate?.checks.find((check) => check.status === 'failed');
  if (failed) return checkBlock(failed, tv);
  const runCode = readRunBlockCode(tv);
  if (runCode) {
    const runSituation = situationForCode(runCode);
    if (runSituation) return blockOf(runSituation, runCode);
    const runReason = tv.reason ? REASON_LABELS[tv.reason] : undefined;
    return { label: runReason ?? NO_VERDICT, code: runCode, worded: false };
  }
  const reason = reasonLabel(tv);
  return reason ? { label: reason, worded: true } : undefined;
}

/** What kept TIA from compiling, English then Chinese: readFirstBlock's phrase. */
export function readBlockerLabel(tv: TiaValidationMetadata): [string, string] | undefined {
  return readFirstBlock(tv)?.label;
}

/**
 * The codes a wait ends on when the reader chooses a project, not when an
 * Agent gets ready: several projects open with none chosen (E7 ②), or the
 * chosen one gone (E2).
 */
const PROJECT_CHOICE_CODES: ReadonlySet<string> = new Set([
  TIA_VALIDATION_CODES.TIA_PROJECT_NOT_SELECTED,
  TIA_VALIDATION_CODES.TIA_PROJECT_REF_NOT_FOUND,
]);

/** A waiting run that waits for the reader to choose a project. */
export function waitsOnProjectChoice(tv: TiaValidationMetadata): boolean {
  if (readOutcome(tv) !== 'waiting') return false;
  const code = readFirstBlock(tv)?.code;
  return Boolean(code && PROJECT_CHOICE_CODES.has(code.toUpperCase()));
}

/**
 * What such a wait says it waits for. A choice in the picker resumes the run
 * (ChatInput passes the picker's onProjectChosen to T8's endpoint, which
 * stores the choice and resumes it), and the verdict follows when the run
 * ends; the words promise nothing sooner.
 */
export const WAITING_ON_PROJECT_CHOICE: [string, string] = ['Waiting for you to choose a project', '等待你选择工程'];

// ---------------------------------------------------------------------------
// The verdict: what a compile ran against (TIA 根治 P2 E5 ①, E6, E8–E10)
// ---------------------------------------------------------------------------

/**
 * Why the gateway gave the program-cycle OB the name it compiled under (E6
 * ②⑥), from the verdict's `programObRename`, English then Chinese: said
 * after 「从 "a" 改名为 "b"」.
 */
const PROGRAM_OB_RENAME_REASONS: Readonly<Record<TiaProgramObRenameReason, [string, string]>> = {
  target_program_ob: [
    'the target project already holds a RealPLC program-cycle OB of that name, which this program replaces.',
    '目标工程里已有这个名字的 RealPLC 程序循环 OB，本程序替换它。',
  ],
  main_free: [
    'the target project\'s OB1 is empty, or the project is new, so this program\'s OB is OB1.',
    '目标工程的 OB1 空着，或者是新建的工程，本程序的 OB 就是 OB1。',
  ],
  main_has_user_logic: [
    'the target project\'s OB1 holds your own program, and this program\'s OB runs beside it.',
    '目标工程的 OB1 里有你自己的程序，本程序的 OB 与它一起执行。',
  ],
  main_unknown: [
    'whether the target project\'s OB1 holds a program could not be read, so this program\'s OB runs beside it.',
    '无法确认目标工程的 OB1 里有没有程序，本程序的 OB 与它一起执行。',
  ],
  worker_expected_name: [
    'the Agent found a RealPLC program-cycle OB of that name in the target project, which this program replaces.',
    'Agent 在目标工程里找到了这个名字的 RealPLC 程序循环 OB，本程序替换它。',
  ],
  worker_main_has_user_logic: [
    'the Agent found your own program in the target project\'s OB1, and this program\'s OB runs beside it.',
    'Agent 发现目标工程的 OB1 里有你自己的程序，本程序的 OB 与它一起执行。',
  ],
};

/**
 * The verdict's own error/warning counts, from `diagnosticsSummary` — the same
 * field `TiaValidationTrace` reads for its facts line, moved here (TA8) so the
 * turn's own native-compile step can state the same numbers without a second,
 * possibly drifting, copy of this reading.
 */
export function readTiaCompileCounts(tv: TiaValidationMetadata): { errors: number; warnings: number } {
  const ds = (tv.diagnosticsSummary ?? {}) as Record<string, unknown>;
  return {
    errors: typeof ds.errorCount === 'number' ? ds.errorCount : 0,
    warnings: typeof ds.warningCount === 'number' ? ds.warningCount : 0,
  };
}

/**
 * The CPU TIA compiled for: the verdict's `target`, which T8 fills from the
 * job result (E9 ⑥); else the run's final summary, the gateway's
 * CompileSummary of that same result, whose `cpu` is the Worker's
 * `Data.plc.typeName` (openness-diagnostics extractSummary). A verdict from
 * before P2 has only the latter.
 */
export function readCompiledCpu(tv: TiaValidationMetadata): string | undefined {
  const fromTarget = tv.target?.cpuModel?.trim();
  if (fromTarget) return fromTarget;
  const run = tv.detail?.run;
  const summary = run && typeof run === 'object' ? (run as { finalSummary?: unknown }).finalSummary : undefined;
  const cpu = summary && typeof summary === 'object' ? (summary as { cpu?: unknown }).cpu : undefined;
  return typeof cpu === 'string' && cpu.trim() ? cpu.trim() : undefined;
}

/**
 * Where the session's CPU came from, said beside the CPU (E9 ⑥), English then
 * Chinese: in a verdict's headline, and in the project picker's 「新建工程」.
 * The default says it in a note of its own instead (TIA_DEFAULT_CPU_NOTE, E9 ③).
 */
export const CPU_SOURCE_WORDS: Readonly<Record<Exclude<PlcModelSource, 'default'>, [string, string]>> = {
  requirement: ['from the requirement', '来自需求'],
  settings: ['from the settings', '来自设置'],
  project: ['from the selected project', '来自所选工程'],
};

/**
 * A compile's headline, English then Chinese, naming the TIA version it
 * compiled with (E8) and the CPU it compiled for, with where that CPU came
 * from (E9 ⑥), when the verdict says them.
 */
export function tiaVerdictTitle(tv: TiaValidationMetadata, passed: boolean): [string, string] {
  const version = tv.target?.tiaVersion?.trim();
  const cpu = readCompiledCpu(tv);
  const cpuSource = tv.target?.cpuSource;
  const source = cpu && cpuSource && cpuSource !== 'default' ? CPU_SOURCE_WORDS[cpuSource] : undefined;
  const cpuEn = cpu && (source ? `${cpu} (${source[0]})` : cpu);
  // A full-width parenthesis needs no space after it: 「按 CPU 1214C（来自需求）编译通过」.
  const cpuZh = cpu && (source ? `按 ${cpu}（${source[1]}）` : `按 ${cpu} `);
  const portal = version ? `TIA Portal ${version}` : 'TIA Portal';
  return [
    `${portal} compile ${passed ? 'passed' : 'failed'}${cpuEn ? ` for ${cpuEn}` : ''}`,
    `${portal} ${cpuZh ?? ''}编译${passed ? '通过' : '未通过'}`,
  ];
}

/**
 * The newest TIA Portal RealPLC has run validations on (E8). V19 and later can
 * be selected, and their verdicts say they are untested.
 */
const LAST_TESTED_TIA_MAJOR = 18;

/** The major version of a TIA version as the Agent writes it: 'V18', 'V19 Update 1', '20.0'. */
function tiaMajorVersion(version: string): number | undefined {
  const major = /^V?\s*(\d+)/i.exec(version)?.[1];
  return major === undefined ? undefined : Number(major);
}

/**
 * What a verdict adds under its headline, English then Chinese, from what the
 * compile ran against (`target`):
 * - a TIA version RealPLC has not tested, V19 and later (E8);
 * - the default CPU note, only when the model's source is the default (E9 ③);
 * - the save-as copy the user agreed to, which leaves their TIA window on the
 *   copy (E5 ①);
 * - an unsaved-changes check that could not be read (E5 ①);
 * - the project's blocks of the same name the validation replaced (E10 ②);
 * - compile errors in the project's other blocks, which do not count against
 *   this program, by block (E10 ③);
 * - a program-cycle OB the gateway renamed, and why (E6 ②⑥).
 */
export function readVerdictNotes(tv: TiaValidationMetadata): Array<[string, string]> {
  const target = tv.target;
  if (!target) return [];
  const notes: Array<[string, string]> = [];
  const version = target.tiaVersion?.trim();
  const major = version ? tiaMajorVersion(version) : undefined;
  if (version && major !== undefined && major > LAST_TESTED_TIA_MAJOR) {
    notes.push([
      `TIA Portal ${version}: this version has not been tested by RealPLC.`,
      `TIA Portal ${version}：此版本未经 RealPLC 实测。`,
    ]);
  }
  if (target.cpuSource === 'default') notes.push([TIA_DEFAULT_CPU_NOTE.en, TIA_DEFAULT_CPU_NOTE.zh]);
  if (target.copyMethod === 'save_as') {
    notes.push([
      'This run used the save-as copy you agreed to, so your TIA window is now on the copy: go back to the original project to keep working.',
      '这次按你的同意改用了另存副本，你的 TIA 窗口现在停在副本上，请回到原工程继续工作。',
    ]);
  }
  if (target.unsavedCheck === 'unknown') {
    notes.push([
      'Whether the project had unsaved changes could not be read, so the copy is the saved version.',
      '无法确认有没有未保存的修改，副本按已保存的版本。',
    ]);
  }
  const replaced = (target.replacedBlocks ?? []).filter((name) => name.trim());
  if (replaced.length > 0) {
    notes.push([
      `Validation replaced the project's blocks of the same name: ${replaced.join(', ')}. Importing the program into your own project replaces them there too.`,
      `验证时替换了工程里的同名块：${replaced.join('、')}；把程序导入你自己的工程时，TIA 也会替换它们。`,
    ]);
  }
  // The blocks by name, with the errors' count when the verdict gives it; the
  // errors themselves are the run's diagnostics' (T8).
  const otherErrors = target.otherBlockErrors ?? 0;
  const otherBlocks = (target.otherBlockNames ?? []).filter((name) => name.trim());
  if (otherErrors > 0 || otherBlocks.length > 0) {
    const countEn = otherErrors > 0 ? `${otherErrors} compile error(s)` : 'compile errors';
    const countZh = otherErrors > 0 ? `${otherErrors} 个错误` : '';
    const blocksEn = otherBlocks.length > 0 ? ` in ${otherBlocks.join(', ')}` : '';
    const blocksZh = otherBlocks.length > 0 ? otherBlocks.join('、') : '';
    const whereZh = [blocksZh, countZh].filter(Boolean).join('，');
    notes.push(replaced.length > 0
      ? [
        `With those blocks replaced, other blocks of your project have ${countEn}${blocksEn}, not counted against this program.`,
        `替换同名块后，你工程里的其他块编译出错：${whereZh}，不计入本程序。`,
      ]
      : [
        `Other blocks of your project have ${countEn}${blocksEn}, not counted against this program.`,
        `你工程里的其他块编译出错：${whereZh}，不计入本程序。`,
      ]);
  }
  const rename = target.programObRename;
  if (rename?.from?.trim() && rename.to?.trim() && rename.from !== rename.to) {
    const [whyEn, whyZh] = PROGRAM_OB_RENAME_REASONS[rename.reason] ?? ['', ''];
    notes.push([
      `The program-cycle OB was renamed from "${rename.from}" to "${rename.to}"${whyEn ? `: ${whyEn}` : '.'}`,
      `程序循环 OB 从 "${rename.from}" 改名为 "${rename.to}"${whyZh ? `：${whyZh}` : '。'}`,
    ]);
  }
  return notes;
}

// ---------------------------------------------------------------------------
// The words the browser's own readiness adds to the readiness table
// (readTiaReadiness in services/opennessAgent.ts); the connector's causes are
// with the situations above, which say them for the gate too.
// ---------------------------------------------------------------------------

/**
 * A conversation in no RealPLC project, while nothing of the Agent's own is
 * in the way (T4 review; readTiaReadiness keeps an Agent's own faults in
 * their words). Nothing can be validated there, the validation route taking a
 * project; yet without one the target resolver checks no pairing binding, so
 * the Agent itself reads ready.
 */
export const TIA_NO_PROJECT_TEXT: { label: TiaText; explanation: TiaText } = {
  label: { zh: '在线 · 未绑定项目', en: 'Online · No project' },
  explanation: {
    zh: '当前对话没有绑定 RealPLC 项目，不能在 TIA 里编译验证，请在项目对话里验证。',
    en: 'This conversation is in no RealPLC project, so nothing can be compiled in TIA; validate in a project conversation.',
  },
};

// ---------------------------------------------------------------------------
// How the Agent validates and creates projects (TIA 根治 P2 E12)
// ---------------------------------------------------------------------------

/**
 * The work mode in words, once: the user setting in the engineering context
 * panel, and where the project picker's 「新建工程」 says the project is made.
 * In neither mode does the Agent drive the user's own TIA window (E12).
 */
export const TIA_WORK_MODE_TEXT: {
  label: TiaText;
  options: Readonly<Record<TiaWorkMode, TiaText>>;
  explanation: TiaText;
  newProject: Readonly<Record<TiaWorkMode, TiaText>>;
} = {
  label: { zh: 'TIA 验证与新建工程', en: 'TIA validation and new projects' },
  options: {
    background: { zh: '在后台（无界面，默认）', en: 'In the background (no window, default)' },
    window: { zh: '在 TIA 窗口里', en: 'In a TIA window' },
  },
  explanation: {
    zh: '窗口方式会另开 TIA 窗口、占更多内存；两种方式都不会动你自己开着的窗口。',
    en: 'The window mode opens a TIA window of its own and uses more memory; neither mode touches the TIA windows you have open.',
  },
  newProject: {
    background: {
      zh: '在后台新建，不开窗口，也不动你的 TIA 窗口',
      en: 'Created in the background, with no window, leaving your TIA windows alone',
    },
    window: {
      zh: '在 Agent 另开的 TIA 窗口里新建并打开',
      en: 'Created and opened in a TIA window the Agent opens for it',
    },
  },
};
